import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { api, fullName, formatEUR, formatDate } from '../lib/api';
import { PageHeader, StatusBadge, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Plus, FileText, Trash2, Stethoscope, ListChecks, Package, CalendarDays, UserPlus, Check, Sparkles, ChevronsUpDown, Pencil, MessageCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import PdfPreviewDialog from '../components/PdfPreviewDialog';
import PdfLangButton from '../components/PdfLangButton';
import BudgetCrmDialog from '../components/BudgetCrmDialog';
import WhatsAppPaqueteButton from '../components/WhatsAppPaqueteButton';
import NewBudgetDialog from '../components/NewBudgetDialog';
import { PatientCombobox } from '../components/PatientCombobox';
import { error as logError } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Caducado = su validez ya pasó, lo diga o no la etiqueta de estado.
 *
 * Esta pantalla sí recibe el estado recalculado (GET /api/budgets caduca al
 * vuelo los «pendiente» y los «sent»), pero no toca a los aceptados: uno
 * aceptado en enero con validez hasta marzo sigue diciendo «Aceptado» hoy.
 * Por eso manda la FECHA, igual que en la ficha del paciente, donde hay una
 * copia de esta misma función: la regla de qué está caducado tiene que ser
 * una sola, y mientras no viva en un sitio común las dos dicen lo mismo,
 * palabra por palabra. El último día válido es el propio `valid_until`.
 */
function presupuestoCaducado(b) {
  if (!b) return false;
  if (b.status === 'expirado') return true;
  const hasta = String(b.valid_until || '').slice(0, 10);
  if (!hasta) return false;
  const d = new Date();
  const hoy = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return hasta < hoy;
}

export default function BudgetsPage() {
  const [searchParams] = useSearchParams();
  const initialPatient = searchParams.get('patient');
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(!!initialPatient);
  const [filter, setFilter] = useState('all');
  const [preview, setPreview] = useState({ open: false, url: '', filename: '', title: '' });
  // Phase 24.D — CRM dialog (estado del presupuesto + pagos parciales)
  const [crmBudget, setCrmBudget] = useState(null);
  // Edición de un presupuesto ya creado (cirugía e importes)
  const [editBudget, setEditBudget] = useState(null);
  // Caducados que alguien ha decidido mandar de todas formas. Dura lo que
  // dura la pantalla abierta: mañana el precio estará un día más vencido.
  const [caducadosDesbloqueados, setCaducadosDesbloqueados] = useState([]);
  const desbloquearEnvioCaducado = (b) => {
    // El clic de más (2026-09-15): que mandar un precio vencido sea
    // una decisión y no un descuido. Verlo en PDF sigue siendo gratis.
    const aviso = `El presupuesto ${b.number} caducó el ${formatDate(b.valid_until)}.\n\n`
      + 'Si lo mandas, la paciente lo recibe con la validez vencida bajo el texto '
      + '«Le hacemos llegar su presupuesto». Lo normal es editarlo y renovar la '
      + 'validez antes de enviarlo.\n\n¿Mandarlo igual, caducado?';
    if (!window.confirm(aviso)) return;
    setCaducadosDesbloqueados((ids) => (ids.includes(b.id) ? ids : [...ids, b.id]));
  };
  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/budgets');
      setItems(res.data);
    } finally { setLoading(false); }
  }, []);

  const fetchPatients = useCallback(async () => {
    try {
      const res = await api.get('/api/patients');
      setPatients(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      logError('[BudgetsPage] No se pudieron pre-cargar pacientes:', e?.message || e);
      setPatients([]);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    fetchPatients();
  }, [fetchItems, fetchPatients]);

  // El backend crea con 'pendiente' y caduca a 'expirado'; el CRM usa
  // draft/sent/accepted/rejected. Se normaliza para filtrar y contar.
  const estado = (b) => ({ pendiente: 'draft', confirmado: 'accepted', cancelado: 'rejected' }[b.status] || b.status || 'draft');
  const visible = items.filter(b => filter === 'all' ? true : estado(b) === filter);

  // Seguimiento comercial (2026-08-27): ratio de conversión entre
  // presupuestos enviados y aceptados. Un aceptado cuenta también como
  // enviado (llegó al paciente).
  const nEnviados = items.filter(b => ['sent', 'accepted'].includes(estado(b))).length;
  const nAceptados = items.filter(b => estado(b) === 'accepted').length;
  const ratio = nEnviados > 0 ? Math.round((100 * nAceptados) / nEnviados) : null;

  // eslint-disable-next-line no-unused-vars
  const changeStatus = async (b, status) => {
    await api.put(`/api/budgets/${b.id}`, { status });
    toast.success('Estado actualizado');
    fetchItems();
  };
  // El aviso dice QUÉ se borra: el número y de quién. Borrar ya no renumera a
  // los demás —cada uno se llama por su PRES-—, pero sigue sin haber vuelta
  // atrás, y «¿Eliminar presupuesto?» a secas no deja comprobar nada.
  const onDelete = async (b) => {
    if (!window.confirm(`¿Eliminar el presupuesto ${b.number} de ${b.patient_name}? No se puede deshacer.`)) return;
    await api.delete(`/api/budgets/${b.id}`);
    toast.success('Eliminado');
    fetchItems();
  };

  // Re-fetch the patient list whenever the dialog opens so a patient created
  // in a different tab/window is immediately available without page refresh.
  useEffect(() => {
    if (dialogOpen) fetchPatients();
  }, [dialogOpen, fetchPatients]);

  return (
    <div>
      <PageHeader title="Presupuestos" subtitle={`${items.length} presupuestos generados`}
        actions={<Button onClick={() => setDialogOpen(true)} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="budget-new-button"><Plus className="w-4 h-4 mr-2" /> Nuevo presupuesto</Button>} />

      <div className="cp-card p-3 mb-4 flex flex-wrap items-center gap-1" data-testid="budget-filter">
        {[['all', 'Todos'], ['draft', 'Borrador'], ['sent', 'Enviados'], ['accepted', 'Aceptados'], ['rejected', 'Rechazados'], ['expirado', 'Caducados']].map(([s, label]) => (
          <Button key={s} size="sm" variant={filter === s ? 'default' : 'ghost'} onClick={() => setFilter(s)} className={filter === s ? 'bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white' : ''}>
            {label}
          </Button>
        ))}
        <div className="flex-1" />
        {/* Ratio de conversión enviados → aceptados (2026-08-27). */}
        <div className="text-sm px-3 py-1 rounded-md bg-[rgba(46,110,94,0.08)] text-[#13231E]" data-testid="budget-conversion" title="Aceptados sobre el total que llegó al paciente (enviados + aceptados)">
          Conversión: <strong>{ratio === null ? '—' : `${ratio}%`}</strong>
          <span className="text-muted-foreground"> · {nAceptados} aceptados de {nEnviados} enviados</span>
        </div>
      </div>

      <div className="cp-card overflow-hidden">
        {loading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
          : visible.length === 0 ? <EmptyState icon={FileText} title="Sin presupuestos" description="Crea tu primer presupuesto." />
          : <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[rgba(46,110,94,0.04)] text-left">
                <tr><th className="py-3 px-4 font-semibold text-[#13231E]">Nº</th><th className="py-3 px-4 font-semibold text-[#13231E]">Paciente</th><th className="py-3 px-4 font-semibold text-[#13231E]">Fecha</th><th className="py-3 px-4 font-semibold text-[#13231E]">Vence</th><th className="py-3 px-4 font-semibold text-[#13231E] text-right">Total</th><th className="py-3 px-4 font-semibold text-[#13231E]">Estado</th><th></th></tr>
              </thead>
              <tbody>
                {visible.map(b => {
                  const caducado = presupuestoCaducado(b);
                  const puedeEnviarse = !caducado || caducadosDesbloqueados.includes(b.id);
                  return (
                  <tr key={b.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="budget-row">
                    <td className="py-3 px-4 font-mono text-xs">
                      {b.number}
                      {/* Antes esto iba escrito en las notas del presupuesto y
                          se imprimía en el papel del paciente (2026-07-28).
                          Aquí avisa a quien lo gestiona, no al paciente. */}
                      {b.origen === 'evolutivo' && (
                        <span
                          className="ml-1.5 inline-block rounded px-1.5 py-[1px] text-[9px] font-sans font-bold uppercase tracking-wide bg-amber-100 text-amber-900 border border-amber-300 align-middle"
                          title="Salió del evolutivo automáticamente: revísalo antes de enviarlo"
                          data-testid="budget-origen-evolutivo"
                        >
                          Del evolutivo · revisar
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 font-medium">{b.patient_name}</td>
                    <td className="py-3 px-4 text-xs">{formatDate(b.date)}</td>
                    {/* La etiqueta de estado no siempre lo canta (un aceptado
                        vencido sigue poniendo «Aceptado»), así que la fecha
                        de vencimiento lo dice ella misma. */}
                    <td className="py-3 px-4 text-xs">
                      {formatDate(b.valid_until)}
                      {caducado && (
                        <span
                          className="ml-1.5 inline-flex items-center gap-1 rounded-full border px-1.5 py-[1px] text-[10px] font-semibold bg-amber-100 text-amber-900 border-amber-300 align-middle"
                          data-testid="budget-caducado"
                          title={`Su validez venció el ${formatDate(b.valid_until)}`}
                        >
                          <AlertCircle className="w-3 h-3" /> Caducado
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold text-[#13231E] tabular-nums">{formatEUR(b.total)}</td>
                    <td className="py-3 px-4"><StatusBadge status={b.status} /></td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex gap-1 items-center justify-end flex-wrap">
                        <PdfLangButton
                          baseUrl={`${BACKEND_URL}/api/budgets/${b.id}/pdf`}
                          filename={`presupuesto_${b.number}.pdf`}
                          title={`Presupuesto ${b.number} · ${b.patient_name}`}
                          onOpen={(p) => setPreview({ open: true, ...p })}
                          dataTestid="budget-pdf-button"
                        >
                          <Button size="sm" variant="outline" title="Ver presupuesto (elige idioma)"><FileText className="w-4 h-4" /></Button>
                        </PdfLangButton>
                        {b.preop_id && (
                          <PdfLangButton
                            baseUrl={`${BACKEND_URL}/api/preop/${b.preop_id}/pdf`}
                            filename={`preop_${b.number}.pdf`}
                            title={`Preoperatorio · ${b.patient_name}`}
                            onOpen={(p) => setPreview({ open: true, ...p })}
                            dataTestid="budget-preop-button"
                          >
                            <Button size="sm" variant="outline" title="Ver solicitud de pruebas preoperatorias (elige idioma)"><Stethoscope className="w-4 h-4" /></Button>
                          </PdfLangButton>
                        )}
                        {b.instructions_id && (
                          <PdfLangButton
                            baseUrl={`${BACKEND_URL}/api/instructions/${b.instructions_id}/pdf`}
                            filename={`indicaciones_${b.number}.pdf`}
                            title={`Indicaciones · ${b.patient_name}`}
                            onOpen={(p) => setPreview({ open: true, ...p })}
                            dataTestid="budget-instructions-button"
                          >
                            <Button size="sm" variant="outline" title="Ver indicaciones día de cirugía (elige idioma)"><ListChecks className="w-4 h-4" /></Button>
                          </PdfLangButton>
                        )}
                        {(b.preop_id || b.instructions_id) && (
                          <PdfLangButton
                            baseUrl={`${BACKEND_URL}/api/budgets/${b.id}/bundle-pdf`}
                            filename={`paquete_${b.number}.pdf`}
                            title={`Paquete completo · ${b.patient_name}`}
                            onOpen={(p) => setPreview({ open: true, ...p })}
                            dataTestid="budget-bundle-button"
                          >
                            <Button size="sm" className="bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white" title="Paquete completo (elige idioma)"><Package className="w-4 h-4 mr-1" /> Paquete</Button>
                          </PdfLangButton>
                        )}
                        {/* Ver el PDF es gratis; mandar uno vencido cuesta un
                            clic más. Mientras está caducado, en el sitio del
                            botón de WhatsApp hay uno ámbar que lo dice y
                            pregunta (2026-09-15). */}
                        {!puedeEnviarse ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100"
                            onClick={() => desbloquearEnvioCaducado(b)}
                            data-testid="budget-caducado-gate"
                            title={`Caducó el ${formatDate(b.valid_until)}: no se manda por descuido. Pulsa para decidirlo.`}
                          >
                            <AlertCircle className="w-4 h-4" />
                          </Button>
                        ) : (
                          <WhatsAppPaqueteButton budget={b} onSent={fetchItems} dataTestid="budget-whatsapp">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10"
                              title="Paquete online: presupuesto + pruebas preoperatorias por WhatsApp (elige idioma)"
                            >
                              <MessageCircle className="w-4 h-4" />
                            </Button>
                          </WhatsAppPaqueteButton>
                        )}
                        {/* Phase 24.D — Botón único de gestión CRM (envío, aceptación, rechazo, pagos) */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setCrmBudget(b)}
                          className="border-[#2E6E5E] text-[#13231E] hover:bg-[rgba(46,110,94,0.08)]"
                          data-testid="budget-crm-open"
                          title="Gestionar estado y pagos"
                        >
                          Estado / Pagos
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditBudget(b)}
                          title="Editar cirugía e importes"
                          data-testid="budget-edit-open"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => onDelete(b)} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        }
      </div>

      <NewBudgetDialog open={dialogOpen} onOpenChange={setDialogOpen} initialPatient={initialPatient} patients={patients} onPatientsRefresh={fetchPatients} onCreated={fetchItems} />
      <NewBudgetDialog
        open={!!editBudget}
        onOpenChange={(v) => { if (!v) setEditBudget(null); }}
        budget={editBudget}
        patients={patients}
        onCreated={fetchItems}
      />
      <PdfPreviewDialog open={preview.open} onOpenChange={(v) => setPreview(s => ({ ...s, open: v }))} url={preview.url} filename={preview.filename} title={preview.title} />
      <BudgetCrmDialog
        open={!!crmBudget}
        onOpenChange={(v) => { if (!v) setCrmBudget(null); }}
        budget={crmBudget}
        onChanged={fetchItems}
      />
    </div>
  );
}
